import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceCTA from "../../components/ServiceCTA";
import ServiceHero from "../../components/ServiceHero";
import ContentImageSection from "../../components/sections/ContentImageSection";
import { CardColumn } from "../../components/sections";
import { FiZap, FiMessageSquare, FiEye, FiRefreshCw } from 'react-icons/fi';
import bgImage from "../../images/img1.jpg";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const AIDevelopmentPage = () => {
  usePageMetadata({
    title: 'AI Development - Bercastle',
    description: 'Custom artificial intelligence solutions including machine learning, NLP, computer vision, and intelligent automation. Building the technology behind OnScript AI.',
    pageType: 'service',
    content: {
      service: {
        name: "AI Development",
        tagline: "Building Production-Ready AI Solutions",
        description: "Custom artificial intelligence solutions including machine learning models, natural language processing, and intelligent automation. Building the technology behind OnScript AI.",
        url: "/services/ai-development"
      },
      capabilities: [
        {
          name: "Machine Learning",
          description: "Custom ML models for prediction, classification, and pattern recognition. From data preparation to model deployment and monitoring.",
          features: [
            "Predictive analytics and forecasting",
            "Classification and clustering algorithms",
            "Real-time model deployment",
            "Continuous learning and optimization",
            "Model performance monitoring"
          ]
        },
        {
          name: "Natural Language Processing",
          description: "Advanced NLP solutions including sentiment analysis, text generation, language understanding, and conversational AI systems.",
          features: [
            "Sentiment analysis and opinion mining",
            "Text classification and categorization",
            "Named entity recognition",
            "Chatbots and virtual assistants",
            "Language translation",
            "Text summarization"
          ]
        },
        {
          name: "Computer Vision",
          description: "Image and video analysis, object detection, facial recognition, and visual search capabilities powered by deep learning.",
          features: [
            "Object detection and tracking",
            "Facial recognition systems",
            "Image classification",
            "Visual quality inspection",
            "OCR and document processing",
            "Video analytics"
          ]
        },
        {
          name: "Intelligent Automation",
          description: "AI-powered workflow automation, decision systems, and process optimization that reduces manual work and increases efficiency.",
          features: [
            "Automated decision-making systems",
            "Workflow optimization",
            "Process automation",
            "Intelligent document processing",
            "Business rules engines",
            "Robotic process automation (RPA)"
          ]
        }
      ],
      case_study: {
        name: "OnScript AI",
        description: "OnScript AI is a cutting-edge platform that revolutionizes content creation and workflow automation. We built the core AI technology infrastructure.",
        challenge: "Build a scalable AI infrastructure capable of handling complex content generation workflows in real-time.",
        solution: "Developed custom NLP models, implemented intelligent automation systems, and created a robust API infrastructure.",
        results: [
          "Real-time content generation",
          "Scalable architecture handling thousands of requests",
          "Advanced NLP capabilities",
          "Seamless workflow automation",
          "Robust API infrastructure"
        ],
        url: "https://onscript.ai"
      },
      value_propositions: [
        "Production-Grade Systems: We build AI that works in real-world conditions",
        "Deep Technical Expertise: Our team combines ML engineering with strategic thinking",
        "End-to-End Solutions: From data prep to deployment and monitoring",
        "Proven Track Record: Successfully built OnScript AI platform",
        "Scalable Architecture: Systems designed to grow with your business"
      ],
      meta: {
        source_url: "https://bercastle.com/services/ai-development",
        service_category: "AI Development"
      }
    }
  });
  const aiCapabilities = [
    {
      icon: FiZap,
      title: 'Machine Learning',
      description: 'Custom ML models for prediction, classification, and pattern recognition. From data preparation to model deployment and monitoring.'
    },
    {
      icon: FiMessageSquare,
      title: 'Natural Language Processing',
      description: 'Advanced NLP solutions including sentiment analysis, text generation, language understanding, and conversational AI systems.'
    },
    {
      icon: FiEye,
      title: 'Computer Vision',
      description: 'Image and video analysis, object detection, facial recognition, and visual search capabilities powered by deep learning.'
    },
    {
      icon: FiRefreshCw,
      title: 'Intelligent Automation',
      description: 'AI-powered workflow automation, decision systems, and process optimization that reduces manual work and increases efficiency.'
    }
  ];
  return (
    <Layout classmain="ai-development-page">
      <Seo
        title='AI Development - Bercastle'
        metaDesciption='Custom artificial intelligence solutions including machine learning, NLP, computer vision, and intelligent automation. Building the technology behind OnScript AI.'
      />
      <Header />

      <ServiceHero 
        badge="AI Development"
        title="Building Production-Ready"
        titleHighlight="AI Solutions"
        description="Custom artificial intelligence solutions including machine learning models, natural language processing, and intelligent automation."
        ctaLink="/contact"
      />

      {/* Services Detail Section */}
      <div className='py-20 md:py-28'>
        <div className='container-custom'>
          <CardColumn
            title="Our AI Capabilities"
            subtitle="Expert Solutions"
            description="We specialize in building production-grade artificial intelligence systems that solve real business problems. Our team combines deep technical expertise with strategic thinking."
            cols={2}
            cards={aiCapabilities}
          />
        </div>
      </div>

      {/* OnScript AI Case Study */}
      <div className='py-20 md:py-28'>
        <div className='container-custom'>
          <ContentImageSection
            image={bgImage}
            imageAlt="OnScript AI Platform"
          >
            <div>
              <h2 className='text-4xl md:text-5xl mb-6'>OnScript AI</h2>
              <div className='w-20 h-1 bg-brand-red mb-6'></div>
              <div className='space-y-6'>
                <p className='text-xl text-slate-600 leading-relaxed'>
                  OnScript AI is a cutting-edge platform that revolutionizes content creation and workflow automation. We built the core AI technology infrastructure.
                </p>

                <div>
                  <h3 className='font-bold text-lg mb-2'>Challenge</h3>
                  <p className='text-slate-600'>
                    Build a scalable AI infrastructure capable of handling complex content generation workflows in real-time.
                  </p>
                </div>

                <div>
                  <h3 className='font-bold text-lg mb-2'>Solution</h3>
                  <p className='text-slate-600'>
                    Developed custom NLP models, implemented intelligent automation systems, and created a robust API infrastructure.
                  </p>
                </div>

                <a href="https://onscript.ai" target="_blank" rel="noopener noreferrer" className='inline-block mt-4 px-6 py-3 bg-brand-red text-white rounded-lg hover:bg-red-700 transition-colors'>
                  Visit OnScript.ai
                </a>
              </div>
            </div>
          </ContentImageSection>
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Build Your AI Solution?"
        description="Let's discuss how artificial intelligence can transform your business and drive measurable results."
        buttonText="Get in Touch"
      />

      <Footer />
    </Layout>
  )
}

export default AIDevelopmentPage
